import { create } from 'zustand';

/**
 * Vai trò theo BR-801. Admin Web chỉ phục vụ `super_admin` và `branch_manager`;
 * `cashier` và `host` là vai trò của Staff App, đăng nhập web sẽ bị từ chối.
 */
export type StaffRole = 'super_admin' | 'branch_manager' | 'cashier' | 'host';

export const ADMIN_WEB_ROLES: readonly StaffRole[] = ['super_admin', 'branch_manager'];

export interface StaffSession {
  staffId: string;
  name: string;
  role: StaffRole;
  /** null với vai trò HQ (super_admin) — xem 04-data-model.md `staff_users` */
  branchId: string | null;
  accessToken: string;
  /** true thì server chặn mọi endpoint khác cho tới khi đổi mật khẩu */
  mustChangePassword: boolean;
}

/**
 * Refresh token nằm trong localStorage; access token CHỈ trong bộ nhớ.
 *
 * Access Admin Web sống 8 giờ; F5 thì mất access, khôi phục bằng refresh (14 ngày).
 * XSS đọc được refresh token — rủi ro Phase 1, xem `PROJECT-STATE.md`.
 */
const REFRESH_TOKEN_KEY = 'reski.refresh_token';

export function readStoredRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    // Trình duyệt chặn localStorage (chế độ riêng tư, chính sách doanh nghiệp) —
    // app vẫn chạy được, chỉ là mỗi lần tải lại phải đăng nhập lại
    return null;
  }
}

export function storeRefreshToken(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    else window.localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch {
    // Không lưu được thì thôi — không đáng làm hỏng luồng đăng nhập
  }
}

interface SessionState {
  session: StaffSession | null;
  /** false khi còn đang khôi phục phiên lúc mở app — tránh chớp màn đăng nhập rồi nhảy vào trang */
  isRestoring: boolean;
  signIn: (session: StaffSession, refreshToken: string) => void;
  signOut: () => void;
  /** Cập nhật access token sau khi refresh, giữ nguyên phần còn lại của phiên */
  renewAccessToken: (accessToken: string, refreshToken: string) => void;
  markPasswordChanged: () => void;
  finishRestoring: () => void;
}

export const useSession = create<SessionState>((set) => ({
  session: null,
  isRestoring: true,

  signIn: (session, refreshToken) => {
    storeRefreshToken(refreshToken);
    set({ session, isRestoring: false });
  },

  signOut: () => {
    storeRefreshToken(null);
    set({ session: null, isRestoring: false });
  },

  renewAccessToken: (accessToken, refreshToken) => {
    storeRefreshToken(refreshToken);
    set((state) =>
      state.session === null ? state : { session: { ...state.session, accessToken } },
    );
  },

  markPasswordChanged: () =>
    set((state) =>
      state.session === null ? state : { session: { ...state.session, mustChangePassword: false } },
    ),

  finishRestoring: () => set({ isRestoring: false }),
}));

export const useCurrentStaff = () => useSession((state) => state.session);

export function canAccessAdminWeb(session: StaffSession | null): boolean {
  return session !== null && ADMIN_WEB_ROLES.includes(session.role);
}

export const isSuperAdmin = (session: StaffSession | null): boolean =>
  session?.role === 'super_admin';
